import React, { useCallback, useEffect, useRef, useState } from "react";
import ConnectWallet from "@proton/web-sdk";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import "./App.css";
import MarketsList from "./components/MarketsList";
import MarketDetail from "./components/MarketDetail";
import Portfolio from "./components/Portfolio";
import SuperAdminPanel from "./components/SuperAdminPanel";
import { isSuperAdmin } from "./components/AdminResolve";
import Footer from "./components/footer/Footer";
import HowToUse from "./components/howtouse/HowToUse";
import Tooltip from "./components/Tooltip";
import Whitepaper from "./components/whitepaper/Whitepaper";
import WhatIsXpred from "./components/WhatIsXpred/WhatIsXpred";
import NotificationCenter from "./components/NotificationCenter";
import Leaderboard from "./components/Leaderboard";
import MobileLayout from "./components/MobileLayout";
import MarketResolution from "./components/MarketResolution";
import MarketResolutionDetail from "./components/MarketResolutionDetail";
import PlatformIncome from "./components/PlatformIncome";
import AddProposal from "./components/MarketProposalModal";
import MyProposals from "./components/MyProposals";
import UpcomingMarkets from "./components/UpcomingMarkets";
import { TRADING_TOKEN } from "./configs/config";
import {
  appRoutes,
  getLegacyRedirectPath,
  getTopNavSection,
  isResolutionTab,
  parseIntegerParam,
  type ResolutionTab,
} from "./routing";
import { BadgeDollarSign, ChevronDown, ExternalLink, Info } from "lucide-react";

interface AppShellProps {
  session: any;
  sessionLoading: boolean;
  onLogin: () => void | Promise<void>;
  onLogout: () => void | Promise<void>;
}

interface RedirectToHomeProps {
  message: string;
  setLoginMessage: React.Dispatch<React.SetStateAction<string | null>>;
}

interface RequireSessionProps extends RedirectToHomeProps {
  session: any;
  sessionLoading: boolean;
  children: React.ReactElement;
}

interface RequireSuperAdminProps extends RequireSessionProps {}

const ResolutionListRoute: React.FC<{ session: any }> = ({ session }) => {
  const navigate = useNavigate();

  return (
    <MarketResolution
      session={session}
      onSelectMarket={(marketId, tab) =>
        navigate(appRoutes.resolutionDetail(tab, marketId))
      }
    />
  );
};

const ResolutionDetailRoute: React.FC<{ session: any }> = ({ session }) => {
  const navigate = useNavigate();
  const { marketId, tab } = useParams();
  const parsedMarketId = parseIntegerParam(marketId);

  if (!parsedMarketId || !isResolutionTab(tab)) {
    return <Navigate to={appRoutes.resolution} replace />;
  }

  return (
    <MarketResolutionDetail
      session={session}
      marketId={parsedMarketId}
      tab={tab as ResolutionTab}
      onBack={() => navigate(appRoutes.resolution)}
    />
  );
};

const MarketDetailRoute: React.FC<{ session: any }> = ({ session }) => {
  const navigate = useNavigate();
  const { marketId } = useParams();
  const parsedMarketId = parseIntegerParam(marketId);

  if (!parsedMarketId) {
    return <Navigate to={appRoutes.home} replace />;
  }

  return (
    <MarketDetail
      session={session}
      marketId={parsedMarketId}
      onBack={() => navigate(appRoutes.home)}
      onSelectRelatedMarket={(relatedMarketId) =>
        navigate(appRoutes.market(relatedMarketId))
      }
    />
  );
};

const ProposalDetailRoute: React.FC<{ session: any }> = ({ session }) => {
  const navigate = useNavigate();
  const { marketId } = useParams();
  const parsedMarketId = parseIntegerParam(marketId);

  if (!parsedMarketId) {
    return <Navigate to={appRoutes.proposals} replace />;
  }

  return (
    <MyProposals
      session={session}
      initialMarketId={parsedMarketId}
      onBackToList={() => navigate(appRoutes.proposals)}
    />
  );
};

const UpcomingDetailRoute: React.FC<{ session: any }> = ({ session }) => {
  const navigate = useNavigate();
  const { marketId } = useParams();
  const parsedMarketId = parseIntegerParam(marketId);

  if (!parsedMarketId) {
    return <Navigate to={appRoutes.upcoming} replace />;
  }

  return (
    <UpcomingMarkets
      session={session}
      initialMarketId={parsedMarketId}
      onBackToList={() => navigate(appRoutes.upcoming)}
    />
  );
};

const MarketsRoute: React.FC<{ session: any }> = ({ session }) => {
  const navigate = useNavigate();

  return (
    <MarketsList
      session={session}
      onSelectMarket={(marketId) => navigate(appRoutes.market(marketId))}
    />
  );
};

const RedirectToHomeWithMessage: React.FC<RedirectToHomeProps> = ({
  message,
  setLoginMessage,
}) => {
  useEffect(() => {
    setLoginMessage(message);
  }, [message, setLoginMessage]);

  return <Navigate to={appRoutes.home} replace />;
};

const RequireSession: React.FC<RequireSessionProps> = ({
  session,
  sessionLoading,
  message,
  setLoginMessage,
  children,
}) => {
  if (sessionLoading) {
    return null;
  }

  if (!session) {
    return (
      <RedirectToHomeWithMessage
        message={message}
        setLoginMessage={setLoginMessage}
      />
    );
  }

  return children;
};

const RequireSuperAdmin: React.FC<RequireSuperAdminProps> = ({
  session,
  sessionLoading,
  message,
  setLoginMessage,
  children,
}) => {
  if (sessionLoading) {
    return null;
  }

  if (!session) {
    return (
      <RedirectToHomeWithMessage
        message={message}
        setLoginMessage={setLoginMessage}
      />
    );
  }

  if (!isSuperAdmin(session.auth.actor.toString())) {
    return (
      <RedirectToHomeWithMessage
        message="You do not have SuperAdmin privileges."
        setLoginMessage={setLoginMessage}
      />
    );
  }

  return children;
};

export const AppShell: React.FC<AppShellProps> = ({
  session,
  sessionLoading,
  onLogin,
  onLogout,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const walletDropdownRef = useRef<HTMLDivElement>(null);
  const [walletDropdownOpen, setWalletDropdownOpen] = useState<boolean>(false);
  const [loginMessage, setLoginMessage] = useState<string | null>(null);

  const currentAccount = session?.auth?.actor?.toString() ?? "";
  const currentPath = `${location.pathname}${location.search}`;
  const legacyRedirect = getLegacyRedirectPath(
    location.pathname,
    location.search,
  );
  const activeSection = getTopNavSection(location.pathname);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        walletDropdownRef.current &&
        !walletDropdownRef.current.contains(event.target as Node)
      ) {
        setWalletDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (session) {
      setLoginMessage(null);
    }
  }, [session]);

  const handleNavigate = useCallback(
    (to: string) => {
      setLoginMessage(null);
      navigate(to);
    },
    [navigate],
  );

  const handleLogoutClick = useCallback(async () => {
    setWalletDropdownOpen(false);
    setLoginMessage(null);
    await Promise.resolve(onLogout());
    navigate(appRoutes.home);
  }, [navigate, onLogout]);

  if (legacyRedirect && legacyRedirect !== currentPath) {
    return <Navigate to={legacyRedirect} replace />;
  }

  return (
    <MobileLayout
      onNavigate={handleNavigate}
      activeTab={activeSection ?? undefined}
    >
      <div className="App">
        <header className="app-header">
          <Link
            to={appRoutes.home}
            className="app-title-link"
            onClick={() => setLoginMessage(null)}
          >
            <h1>
              <span className="xpr-highlight">XPR</span>
              {(process.env.REACT_APP_NAME || "XPR Prediction Market").replace(
                "XPR",
                "",
              )}
            </h1>
          </Link>
          {process.env.REACT_APP_IS_TEST_SITE === "true" && (
            <div className="test-site-banner">
              THIS IS A TEST SITE ONLY - Join our{" "}
              <a
                href="https://t.me/Homebloks/4029"
                target="_blank"
                rel="noopener noreferrer"
                className="telegram-link"
              >
                Telegram channel
              </a>{" "}
              to participate in development{" "}
              <a
                href="https://t.me/Homebloks/4029"
                target="_blank"
                rel="noopener noreferrer"
                className="telegram-link"
                title="Join our Telegram"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="20"
                  height="20"
                >
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                </svg>
              </a>
            </div>
          )}
          <div className="header-actions">
            {sessionLoading ? (
              <div
                className="wallet-status-placeholder"
                aria-hidden="true"
              />
            ) : !session ? (
              <Tooltip
                text={`Connect your XPR wallet to start trading. You'll need ${TRADING_TOKEN} tokens to place orders.`}
                position="bottom-padded"
              >
                <button onClick={onLogin} className="connect-button">
                  Connect Wallet
                </button>
              </Tooltip>
            ) : (
              <div className="user-info">
                <NotificationCenter account={currentAccount} />
                <div className="wallet-dropdown" ref={walletDropdownRef}>
                  <button
                    className="wallet-dropdown-trigger"
                    onClick={() => setWalletDropdownOpen(!walletDropdownOpen)}
                  >
                    <span className="wallet-name">{currentAccount}</span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={walletDropdownOpen ? "rotated" : ""}
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {walletDropdownOpen && (
                    <div className="wallet-dropdown-menu">
                      <button
                        className="wallet-dropdown-item"
                        onClick={() => {
                          handleNavigate(appRoutes.proposalNew);
                          setWalletDropdownOpen(false);
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M12 4v16m8-8H4" />
                        </svg>
                        Add Market Proposal
                      </button>
                      <button
                        className="wallet-dropdown-item"
                        onClick={() => {
                          handleNavigate(appRoutes.proposals);
                          setWalletDropdownOpen(false);
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        My Prediction Proposals
                      </button>
                      <button
                        className="wallet-dropdown-item"
                        onClick={() => {
                          handleNavigate(appRoutes.portfolio);
                          setWalletDropdownOpen(false);
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 12V7H5a2 2 0 010-4h14v4" />
                          <path d="M3 5v14a2 2 0 002 2h16v-5" />
                          <path d="M18 12a2 2 0 100 4 2 2 0 000-4z" />
                        </svg>
                        Portfolio
                      </button>
                      <div className="wallet-dropdown-divider"></div>
                      <button
                        className="wallet-dropdown-item wallet-dropdown-disconnect"
                        onClick={handleLogoutClick}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                          <polyline points="16 17 21 12 16 7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        Disconnect Wallet
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </header>

        <nav className="nav-tabs">
          <button
            className={activeSection === "markets" ? "active" : ""}
            onClick={() => handleNavigate(appRoutes.home)}
          >
            Markets
          </button>
          <button
            className={activeSection === "upcoming" ? "active" : ""}
            onClick={() => handleNavigate(appRoutes.upcoming)}
            disabled={!session}
          >
            Upcoming Markets
          </button>
          <button
            className={activeSection === "portfolio" ? "active" : ""}
            onClick={() => handleNavigate(appRoutes.portfolio)}
            disabled={!session}
          >
            Portfolio
          </button>
          {session && isSuperAdmin(currentAccount) && (
            <button
              className={activeSection === "superadmin" ? "active" : ""}
              onClick={() => handleNavigate(appRoutes.superadmin)}
              style={{
                backgroundColor:
                  activeSection === "superadmin" ? "#dc2626" : undefined,
              }}
            >
              SuperAdmin
            </button>
          )}
          {/* <button
            className={activeSection === "leaderboard" ? "active" : ""}
            onClick={() => handleNavigate(appRoutes.leaderboard)}
          >
            Leaderboard
          </button> */}
          <button
            className={activeSection === "help" ? "active" : ""}
            onClick={() => handleNavigate(appRoutes.help)}
          >
            How to Use
          </button>
          <div className="nav-dropdown">
            <button
              className={`nav-dropdown-trigger ${activeSection === "whatisxpred" ? "active" : ""}`}
            >
              Buy XPRED
              <ChevronDown />
            </button>
            <div className="nav-dropdown-menu">
              <a
                href="https://proton.alcor.exchange/trade/xpred-tokencreate_xusdc-xtokens"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-dropdown-item external-link"
              >
                <span className="nav-dropdown-icon">
                  <BadgeDollarSign />
                </span>

                <span className="nav-dropdown-text">
                  Buy XPRED at Alcor Exchange
                </span>

                <span className="nav-dropdown-external-icon">
                  <ExternalLink />
                </span>
              </a>

              <button
                type="button"
                className="nav-dropdown-item"
                onClick={() => handleNavigate(appRoutes.xpred)}
              >
                <span className="nav-dropdown-icon">
                  <Info />
                </span>

                <span className="nav-dropdown-text">What is XPRED</span>

                <span className="nav-dropdown-external-icon empty" />
              </button>
            </div>
          </div>

          {session && (
            <button
              className={activeSection === "resolution" ? "active" : ""}
              onClick={() => handleNavigate(appRoutes.resolution)}
            >
              Market Resolution
            </button>
          )}
          {session && (
            <button
              className={activeSection === "income" ? "active" : ""}
              onClick={() => handleNavigate(appRoutes.income)}
            >
              Platform Income
            </button>
          )}
        </nav>

        <main className="main-content">
          {loginMessage && (
            <div className="login-message">
              <p>{loginMessage}</p>
              <button
                onClick={() => setLoginMessage(null)}
                className="dismiss-button"
              >
                Dismiss
              </button>
            </div>
          )}

          <Routes>
            <Route
              path={appRoutes.home}
              element={<MarketsRoute session={session} />}
            />
            <Route
              path="/market/:marketId"
              element={<MarketDetailRoute session={session} />}
            />
            <Route
              path={appRoutes.portfolio}
              element={
                <RequireSession
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to view your portfolio."
                  setLoginMessage={setLoginMessage}
                >
                  <Portfolio session={session} />
                </RequireSession>
              }
            />
            <Route
              path={appRoutes.superadmin}
              element={
                <RequireSuperAdmin
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to access the SuperAdmin panel."
                  setLoginMessage={setLoginMessage}
                >
                  <SuperAdminPanel session={session} />
                </RequireSuperAdmin>
              }
            />
            {/* <Route
              path={appRoutes.leaderboard}
              element={<Leaderboard timeframe="all" />}
            /> */}
            <Route
              path="/profile/:account"
              element={<Navigate to={appRoutes.leaderboard} replace />}
            />
            <Route path={appRoutes.help} element={<HowToUse />} />
            <Route path={appRoutes.whitepaper} element={<Whitepaper />} />
            <Route path={appRoutes.xpred} element={<WhatIsXpred />} />
            <Route
              path={appRoutes.resolution}
              element={
                <RequireSession
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to access Market Resolution."
                  setLoginMessage={setLoginMessage}
                >
                  <ResolutionListRoute session={session} />
                </RequireSession>
              }
            />
            <Route
              path="/resolution/:tab/:marketId"
              element={
                <RequireSession
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to access Market Resolution."
                  setLoginMessage={setLoginMessage}
                >
                  <ResolutionDetailRoute session={session} />
                </RequireSession>
              }
            />
            <Route
              path={appRoutes.income}
              element={
                <RequireSession
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to access Platform Income."
                  setLoginMessage={setLoginMessage}
                >
                  <PlatformIncome session={session} />
                </RequireSession>
              }
            />
            <Route
              path={appRoutes.upcoming}
              element={
                <RequireSession
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to access Upcoming Markets."
                  setLoginMessage={setLoginMessage}
                >
                  <UpcomingMarkets
                    session={session}
                    onNavigateToDetail={(marketId) =>
                      navigate(appRoutes.upcomingDetail(marketId))
                    }
                  />
                </RequireSession>
              }
            />
            <Route
              path="/upcoming/:marketId"
              element={
                <RequireSession
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to access Upcoming Markets."
                  setLoginMessage={setLoginMessage}
                >
                  <UpcomingDetailRoute session={session} />
                </RequireSession>
              }
            />
            <Route
              path={appRoutes.proposalNew}
              element={
                <RequireSession
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to add a market proposal."
                  setLoginMessage={setLoginMessage}
                >
                  <AddProposal
                    session={session}
                    onSuccess={() => navigate(appRoutes.proposals)}
                  />
                </RequireSession>
              }
            />
            <Route
              path={appRoutes.proposals}
              element={
                <RequireSession
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to view your proposals."
                  setLoginMessage={setLoginMessage}
                >
                  <MyProposals
                    session={session}
                    onNavigateToDetail={(marketId) =>
                      navigate(appRoutes.proposalDetail(marketId))
                    }
                  />
                </RequireSession>
              }
            />
            <Route
              path="/proposals/:marketId"
              element={
                <RequireSession
                  session={session}
                  sessionLoading={sessionLoading}
                  message="Please connect your wallet to view proposal details."
                  setLoginMessage={setLoginMessage}
                >
                  <ProposalDetailRoute session={session} />
                </RequireSession>
              }
            />
            <Route
              path="*"
              element={<Navigate to={appRoutes.home} replace />}
            />
          </Routes>
        </main>

        <Footer
          onShowHelp={() => handleNavigate(appRoutes.help)}
          onShowWhitepaper={() => handleNavigate(appRoutes.whitepaper)}
        />
      </div>
    </MobileLayout>
  );
};

function App() {
  const [session, setSession] = useState<any>(null);
  const [sessionLoading, setSessionLoading] = useState<boolean>(true);

  useEffect(() => {
    const attemptRestore = async () => {
      try {
        const { session: restoredSession } = await ConnectWallet({
          linkOptions: {
            endpoints: [
              process.env.REACT_APP_PROTON_ENDPOINT ||
                "https://proton.eosusa.io",
            ],
            chainId:
              process.env.REACT_APP_CHAIN_ID ||
              "71ee83bcf52142d61019d95f9cc5427ba6a0d7ff8accd9e2088ae2abeaf3d3dd",
            restoreSession: true,
            storagePrefix:
              process.env.REACT_APP_NAME || "xpr-prediction-market",
          },
          transportOptions: {
            requestAccount: process.env.REACT_APP_CONTRACT_NAME || "xpredict",
          },
          selectorOptions: {
            appName: process.env.REACT_APP_NAME || "XPR Prediction Market",
            appLogo: `${window.location.origin}/xpr_logo.png`,
          },
        });
        if (restoredSession) {
          setSession(restoredSession);
        }
      } catch (error) {
        console.log("No session to restore");
      } finally {
        setSessionLoading(false);
      }
    };

    attemptRestore();
  }, []);

  const handleLogin = useCallback(async () => {
    try {
      const { session: nextSession } = await ConnectWallet({
        linkOptions: {
          endpoints: [
            process.env.REACT_APP_PROTON_ENDPOINT || "https://proton.eosusa.io",
          ],
          chainId:
            process.env.REACT_APP_CHAIN_ID ||
            "71ee83bcf52142d61019d95f9cc5427ba6a0d7ff8accd9e2088ae2abeaf3d3dd",
          storagePrefix: process.env.REACT_APP_NAME || "xpr-prediction-market",
        },
        transportOptions: {
          requestAccount: process.env.REACT_APP_CONTRACT_NAME || "xpredict",
        },
        selectorOptions: {
          appName: process.env.REACT_APP_NAME || "XPR Prediction Market",
          appLogo: `${window.location.origin}/xpr_logo.png`,
        },
      });
      setSession(nextSession);
    } catch (error) {
      console.error("Login failed:", error);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    if (!session) {
      return;
    }

    try {
      const appIdentifier =
        process.env.REACT_APP_NAME || "proton-prediction-market";
      const chainId =
        process.env.REACT_APP_CHAIN_ID ||
        "71ee83bcf52142d61019d95f9cc5427ba6a0d7ff8accd9e2088ae2abeaf3d3dd";
      await session.link.removeSession(appIdentifier, session.auth, chainId);

      if (typeof window !== "undefined" && window.localStorage) {
        const storagePrefix = appIdentifier;
        localStorage.removeItem(`${storagePrefix}-wallet-type`);
        localStorage.removeItem(`${storagePrefix}-user-auth`);
      }
    } catch (error) {
      console.error("Logout error:", error);
    }

    setSession(null);
  }, [session]);

  return (
    <AppShell
      session={session}
      sessionLoading={sessionLoading}
      onLogin={handleLogin}
      onLogout={handleLogout}
    />
  );
}

export default App;
